import React from 'react'
import { listOportunidades } from '../services/oportunidadesService'
import { fetchThermometerMap } from '@/modules/termometro/services/thermometerService'
import type { OpportunityItem } from '../types'
import { supabase } from '@/lib/supabase'

const CACHE_TTL_MS = 5 * 60 * 1000

let sharedItems: OpportunityItem[] = []
let sharedError: string | null = null
let lastLoadedAt: number | null = null
let inflight: Promise<void> | null = null

export function useOportunidades() {
  const [items, setItems] = React.useState<OpportunityItem[]>(sharedItems)
  const [loading, setLoading] = React.useState(!lastLoadedAt)
  const [error, setError] = React.useState<string | null>(sharedError)

  const load = React.useCallback(async (options?: { force?: boolean }) => {
    const force = options?.force ?? false
    const now = Date.now()

    if (!force && lastLoadedAt && now - lastLoadedAt < CACHE_TTL_MS) {
      setItems(sharedItems)
      setError(sharedError)
      setLoading(false)
      return
    }

    if (!force && inflight) {
      try {
        await inflight
      } catch {
        // erro j� refletido em sharedError
      }
      setItems(sharedItems)
      setError(sharedError)
      setLoading(false)
      return
    }

    setLoading(!lastLoadedAt)
    setError(null)

    inflight = (async () => {
      try {
        const rows = await listOportunidades()
        // Enriquecer com o term�metro calculado no Supabase (se existir)
        try {
          const ids = rows.map((r) => r.id)
          const map = await fetchThermometerMap(ids)
          sharedItems = rows.map((r) => ({
            ...r,
            thermometerPercent: map.get(r.id)?.percent ?? null,
          }))
        } catch {
          sharedItems = rows
        }
        sharedError = null
      } catch (e: any) {
        sharedItems = []
        sharedError = e?.message || 'Falha ao carregar oportunidades'
      } finally {
        lastLoadedAt = Date.now()
        inflight = null
      }
    })()

    try {
      await inflight
    } catch {
      // erro j� aplicado em sharedError
    }

    setItems(sharedItems)
    setError(sharedError)
    setLoading(false)
  }, [])

  React.useEffect(() => { load() }, [load])

  // Auto-refresh suave: atualiza a lista a cada 5 minutos sem exibir loading global
  React.useEffect(() => {
    const id = setInterval(() => {
      load()
    }, CACHE_TTL_MS)
    return () => {
      clearInterval(id)
    }
  }, [load])

  // Realtime: refletir cria��es/atualiza��es/fechamentos rapidamente na lista (SSOT: view dedup)
  React.useEffect(() => {
    let timer: any = null
    const scheduleReload = (force: boolean) => {
      try { if (timer) clearTimeout(timer) } catch {}
      timer = setTimeout(() => { load({ force }) }, 600)
    }
    const channel = supabase
      .channel('opps_list_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'trading_opportunities' }, () => {
        // Debounce para coalescer m�ltiplos eventos de uma mesma rodada de cron
        scheduleReload(false)
      })
      .subscribe()

    // Refresh ao voltar o foco/visibilidade e por evento manual
    const onFocus = () => scheduleReload(false)
    const onVisibility = () => { if (document.visibilityState === 'visible') scheduleReload(false) }
    const onManualRefresh = () => scheduleReload(true)
    try { window.addEventListener('focus', onFocus) } catch {}
    try { document.addEventListener('visibilitychange', onVisibility) } catch {}
    try { window.addEventListener('opportunities:refresh', onManualRefresh as any) } catch {}

    return () => {
      try { supabase.removeChannel(channel) } catch {}
      try { if (timer) clearTimeout(timer) } catch {}
      try { window.removeEventListener('focus', onFocus) } catch {}
      try { document.removeEventListener('visibilitychange', onVisibility) } catch {}
      try { window.removeEventListener('opportunities:refresh', onManualRefresh as any) } catch {}
    }
  }, [load])

  // Realtime: atualiza thermometerPercent assim que o job gravar no banco
  React.useEffect(() => {
    if (!items || items.length === 0) return
    const ids = new Set(
      items
        .map((r) => {
          const n = Number(String(r.id).trim())
          return Number.isFinite(n) ? n : null
        })
        .filter((n): n is number => n !== null)
    )
    const channel = supabase
      .channel('thermo_oportunidades')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'opportunity_thermometer' },
        (payload: any) => {
          const newRow = payload?.new || {}
          const oid = Number(newRow.opportunity_id)
          if (!ids.has(oid)) return
          const percent = Math.round(Math.max(0, Math.min(100, Number(newRow.percent || 0))))
          setItems((prev) => {
            const next = prev.map((it) =>
              Number(String(it.id)) === oid ? { ...it, thermometerPercent: percent } : it,
            )
            sharedItems = next
            return next
          })
        }
      )
      .subscribe()
    return () => {
      try { supabase.removeChannel(channel) } catch {}
    }
  }, [items])

  return { items, loading, error, reload: load }
}
